/**
 * Tangram — drag, rotate and snap pieces into a target silhouette.
 *
 * Pieces are described at their SOLVED position in the unit square, plus the
 * offset they start at in the tray. A piece is placed when its offset and
 * rotation return to zero within `snapTolerance` (rotation about the polygon
 * centroid). Difficulty is carried by how many pieces start already placed,
 * whether rotation is in play, and how much of the outline is drawn in.
 */

import { z } from 'zod'
import { ColorTokenSchema, IdSchema, LabelSchema, PolygonSchema, findDuplicates, specFor } from '../common'

export const TANGRAM_PIECE_SHAPES = [
  'large_triangle',
  'medium_triangle',
  'small_triangle',
  'square',
  'parallelogram',
  'custom',
] as const

export const TANGRAM_GUIDE_MODES = ['silhouette', 'segmented', 'none'] as const

const OffsetSchema = z.number().min(-1).max(1)
const RotationSchema = z.number().min(0).max(359)

export const TangramPieceSchema = z.object({
  id: IdSchema,
  shape: z.enum(TANGRAM_PIECE_SHAPES),
  colorToken: ColorTokenSchema,
  /** The piece drawn where it belongs in the finished picture. */
  polygon: PolygonSchema,
  /** Where it waits before she moves it, as an offset from the solved spot. */
  start: z.object({
    dx: OffsetSchema,
    dy: OffsetSchema,
    rotationDeg: RotationSchema,
    flipped: z.boolean().default(false),
  }),
})

export const TangramConfigSchema = z
  .object({
    pieceSet: z.enum(['classic7', 'custom']),
    pieces: z.array(TangramPieceSchema).min(2).max(12),
    /** The shape she is filling. Give it a name she cares about — "the cat". */
    targetOutline: PolygonSchema,
    targetLabel: LabelSchema,
    /** Pieces already in place when the puzzle opens — the main difficulty dial. */
    prePlacedPieceIds: z.array(IdSchema).default([]),
    /** Normalized distance within which a piece clicks home. */
    snapTolerance: z.number().min(0.01).max(0.3).default(0.08),
    rotationAllowed: z.boolean(),
    rotationSnapDegrees: z.union([z.literal(15), z.literal(45), z.literal(90)]).default(45),
    flipAllowed: z.boolean().default(false),
    guideMode: z.enum(TANGRAM_GUIDE_MODES).default('segmented'),
  })
  .superRefine((config, ctx) => {
    const ids = config.pieces.map((piece) => piece.id)
    for (const duplicate of findDuplicates(ids)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pieces'], message: `duplicate piece id "${duplicate}"` })
    }
    if (config.pieceSet === 'classic7' && config.pieces.length !== 7) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pieces'],
        message: `the classic set has 7 pieces, got ${config.pieces.length}`,
      })
    }
    for (const [index, id] of config.prePlacedPieceIds.entries()) {
      if (!ids.includes(id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['prePlacedPieceIds', index],
          message: `"${id}" is not one of the pieces`,
        })
      }
    }
    if (config.prePlacedPieceIds.length >= config.pieces.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prePlacedPieceIds'],
        message: 'leave at least one piece for her to place',
      })
    }
    if (!config.rotationAllowed && config.pieces.some((piece) => piece.start.rotationDeg !== 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pieces'],
        message: 'pieces start rotated but rotation is switched off, so they can never be solved',
      })
    }
  })

export type TangramConfig = z.infer<typeof TangramConfigSchema>

export const TangramSpecSchema = specFor('tangram', TangramConfigSchema)
